import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import ThumbnailImage from './ThumbnailImage';
import { useLanguage } from '../hooks/useLanguage';

const copy = {
  RU: {
    noPhoto: 'Нет фотографии',
    productFallback: 'Экземпляр',
    detailFallback: 'Деталь',
    productLabel: 'Экземпляр',
    detailLabel: 'Деталь',
  },
  EN: {
    noPhoto: 'No photograph',
    productFallback: 'Catalogue example',
    detailFallback: 'Detail',
    productLabel: 'Example',
    detailLabel: 'Detail',
  },
};

function titleFor(item, lang, labels) {
  const fallback = item.kind === 'detail' ? labels.detailFallback : labels.productFallback;
  const original = item.name || (item.kind === 'detail' ? item.type?.name : '');

  if (lang !== 'EN' || !/[А-Яа-яЁё]/.test(original || '')) {
    return original || `${fallback} #${item.id}`;
  }

  const number = String(item.number ?? '').trim();
  return `${fallback} ${number ? `No. ${number}` : `#${item.id}`}`;
}

function newestPhotoId(item) {
  return (item.photos || []).reduce((latest, photo) => Math.max(latest, Number(photo.id) || 0), 0);
}

export default function LatestNewItems() {
  const { lang, t } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading');

  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  useEffect(() => {
    let active = true;
    setState('loading');

    Promise.all([
      api.get('/products/search', {
        params: { page: 0, size: 6, sort: 'id,desc', q: '' },
      }),
      api.get('/details/page', {
        params: { page: 0, size: 6, sortField: 'id', sortOrder: 'desc' },
      }),
    ]).then(([productsResponse, detailsResponse]) => {
      if (!active) return;

      const products = (Array.isArray(productsResponse.data)
        ? productsResponse.data
        : productsResponse.data?.content || [])
        .map((item) => ({ ...item, kind: 'product' }));
      const details = (Array.isArray(detailsResponse.data)
        ? detailsResponse.data
        : detailsResponse.data?.content || [])
        .map((item) => ({ ...item, kind: 'detail' }));

      const latest = [...products, ...details]
        .sort((left, right) => {
          const photoDifference = newestPhotoId(right) - newestPhotoId(left);
          if (photoDifference !== 0) return photoDifference;
          return Number(right.id || 0) - Number(left.id || 0);
        })
        .slice(0, 6);

      setItems(latest);
      setState('ready');
    }).catch(() => {
      if (active) setState('error');
    });
    return () => { active = false; };
  }, []);

  const allUrl = '/search?q=&searchType=all&page=0&sortField=id&sortOrder=desc&size=20';

  return (
    <section className="recent-section page-width" aria-labelledby="recent-title">
      <header className="section-heading recent-heading">
        <h2 id="recent-title">{t('home.newTitle')}</h2>
        <Link className="text-link recent-all" to={allUrl}>{t('common.viewAll')}</Link>
      </header>

      {state === 'loading' && (
        <div className="recent-grid" aria-label={t('common.loading')}>
          {Array.from({ length: 4 }).map((_, index) => <div className="recent-record recent-record--loading" key={index} />)}
        </div>
      )}

      {state === 'error' && <p className="archive-notice archive-notice--error">{t('common.error')}</p>}
      {state === 'ready' && items.length === 0 && <p className="archive-notice">{t('common.empty')}</p>}

      {state === 'ready' && items.length > 0 && (
        <div className="recent-grid">
          {items.map((item) => {
            const photo = item.photos?.[0]?.url;
            const src = photo?.startsWith('/') ? uploadHost + photo : photo;
            const url = item.kind === 'detail' ? `/details/${item.id}` : `/products/${item.id}`;
            const title = titleFor(item, lang, labels);
            const kindLabel = item.kind === 'detail' ? labels.detailLabel : labels.productLabel;
            const meta = [
              item.year != null ? item.year : null,
              String(item.number ?? '').trim() ? `№ ${item.number}` : null,
              item.manufacturer?.name || null,
            ].filter(Boolean);
            return (
              <Link to={url} className="recent-record" key={`${item.kind}-${item.id}`}>
                <div className="recent-record__image">
                  {src ? <ThumbnailImage src={src} alt={title} /> : <span>{labels.noPhoto}</span>}
                </div>
                <div className="recent-record__body">
                  <div className="recent-record__index">{kindLabel} · {String(item.id).padStart(5, '0')}</div>
                  <h3>{title}</h3>
                  {meta.length > 0 && <p className="recent-record__meta">{meta.join(' · ')}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
